<template>
  <div class="wh" :class="{ 'wh-chrome': showChrome, 'wh-bare': bare }" :style="frameStyle">
    <div v-if="showChrome" class="wh-head">
      <div class="wh-title">{{ placement.config.title }}</div>
      <div class="wh-tools">
        <div v-if="loading" class="wh-spin" />
        <button v-if="canRefresh" type="button" class="wh-refresh" title="Refresh" :disabled="loading" @click="refresh">
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3h-3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </button>
      </div>
    </div>
    <button v-else-if="canRefresh" type="button" class="wh-refresh wh-refresh-float" :class="{ 'is-busy': loading }" title="Refresh" :disabled="loading" @click="refresh">
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v3h-3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
    </button>
    <FilterBar v-if="ownFilters.length" class="wh-filters" :filters="ownFilters" :values="local" @change="setLocal" />
    <div class="wh-body" :style="{ padding: pad + 'px' }">
      <div ref="content" :class="grow ? 'wh-content-grow' : 'wh-content'">
      <div v-if="error" class="wh-error">{{ error }}</div>
      <div v-else-if="loading && !ready" class="wh-loading">Loading…</div>
      <template v-else-if="widget">
        <component
          :is="stock"
          v-if="widget.kind === 'stock' && stock"
          :key="'stock:' + remounts"
          :inputs="inputs"
          :config="placement.config || {}"
          :scope="widgetScope"
          :width="bodyWidth"
          :height="bodyHeight"
          @drill-entity="$emit('drill-entity', $event)"
          @select-patient="$emit('select-patient', $event)"
        />
        <D3Insight
          v-else-if="widget.kind === 'd3'"
          :key="widget.version + ':' + reloads"
          :code="widget.definition.code || ''"
          :inputs="inputRows"
          :min-height="grow ? baseBodyHeight : (widget.definition.min_height || 120)"
          :fixed-height="grow ? 0 : bodyHeight"
          @export="exportSheets"
        />
        <iframe
          v-else-if="widget.kind === 'html'"
          ref="htmlFrame"
          class="wh-frame"
          sandbox="allow-scripts"
          referrerpolicy="no-referrer"
          :srcdoc="htmlDocument"
          title="Widget"
        />
        <div v-else class="wh-error">Unknown widget kind {{ widget.kind }}.</div>
      </template>
      <div v-else class="wh-error">Widget {{ placement.widget }} is not available.</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import D3Insight from '../D3Insight.vue';
import FilterBar from './FilterBar.vue';
import { localDefaults, pageScope } from './pageLayout';
import { stockComponent } from './registry';
import { TOKENS_CSS, FRAME_CSP, demoSafeInputs, jsonForScript } from './frameTokens';
import { exportToExcel } from '@/utils/exportArraytoXlsx';

const EXPORT_MAX_ROWS = 100000;

const SHADOWS = {
  none: 'none',
  sm: '0 1px 3px rgba(26, 25, 24, 0.08)',
  md: '0 4px 14px rgba(26, 25, 24, 0.12)',
};

export default {
  name: 'WidgetHost',
  components: { D3Insight, FilterBar },
  inject: { pageRows: { default: null }, forgetSources: { default: null } },
  props: {
    placement: { type: Object, required: true },
    widget: { type: Object, default: null },
    pageId: { type: Number, default: null },
    sessionId: { type: String, default: '' },
    filters: { type: Object, default: () => ({}) },
    preloaded: { type: Object, default: null },
    spec: { type: Object, default: () => ({}) },
    host: { type: Object, default: () => ({}) },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
    grow: { type: Boolean, default: false },
  },
  emits: ['drill-entity', 'select-patient', 'content-height'],
  data() {
    return { inputs: {}, loading: false, ready: false, error: '', reloads: 0, remounts: 0, local: localDefaults(this.placement) };
  },
  computed: {
    bare() {
      return !!(this.widget && (this.widget.contract || {}).frame === 'own') && !this.showChrome;
    },
    ownFilters() {
      return this.placement.filters || [];
    },
    scope() {
      return pageScope(this.spec, this.placement, this.host, this.filters, this.local);
    },
    widgetScope() {
      const wanted = ((this.widget && this.widget.contract) || {}).params || [];
      return Object.fromEntries(wanted.map((p) => p.name).filter((n) => n in this.scope).map((n) => [n, this.scope[n]]));
    },
    stock() {
      return this.widget && this.widget.kind === 'stock' ? stockComponent(this.widget.component) : null;
    },
    showChrome() {
      return !!(this.placement.config && this.placement.config.title);
    },
    look() {
      return this.placement.style || {};
    },
    pad() {
      return Number(this.look.padding) || 0;
    },
    frameStyle() {
      const look = this.look;
      const style = {};
      if (look.background) style.background = look.background;
      if (look.border_color) style.borderColor = look.border_color;
      if (look.border_width != null) style.borderWidth = look.border_width + 'px';
      if (look.border_radius != null) style.borderRadius = look.border_radius + 'px';
      if (look.shadow) style.boxShadow = SHADOWS[look.shadow] || 'none';
      return style;
    },
    bodyWidth() {
      return Math.max(0, this.width - 2 * this.pad);
    },
    frameExtra() {
      return (this.showChrome ? 34 : 0) + (this.ownFilters.length ? 30 : 0) + 2 * this.pad + (this.bare ? 0 : 2);
    },
    bodyHeight() {
      return Math.max(0, this.height - this.frameExtra + (this.bare ? 0 : 2));
    },
    baseBodyHeight() {
      return Math.max(0, this.placement.layout.h - this.frameExtra + (this.bare ? 0 : 2));
    },
    inputNames() {
      return Object.keys(this.placement.inputs || {});
    },
    sharedSources() {
      return [...new Set(Object.values(this.placement.inputs || {}).map((b) => b && b.source).filter(Boolean))];
    },
    canRefresh() {
      return !this.preloaded && !!this.widget;
    },
    inputRows() {
      const out = {};
      this.inputNames.forEach((name) => { out[name] = (this.inputs[name] || {}).rows || []; });
      return out;
    },
    htmlDocument() {
      const template = (this.widget && this.widget.definition && this.widget.definition.template) || '';
      const inputs = demoSafeInputs(this.inputRows);
      return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">
<style>${TOKENS_CSS}
html,body{margin:0;max-width:100%;overflow-x:hidden}
body{font:14px/1.45 -apple-system,system-ui,sans-serif;color:var(--body);background:var(--card)}</style>
</head><body>
<div id="root">${template}</div>
<script>
window.inputs = ${jsonForScript(inputs)};
window.config = ${jsonForScript(this.placement.config || {})};
window.exportData = function (spec) { parent.postMessage({ kaironExport: JSON.parse(JSON.stringify(spec || {})) }, '*'); };
document.dispatchEvent(new Event('kairon:ready'));
<\/script>
</body></html>`;
    },
  },
  watch: {
    scope: { deep: true, handler() { this.load(); } },
    'placement.inputs': { deep: true, handler() { this.load(); } },
  },
  mounted() {
    window.addEventListener('message', this.onFrameMessage);
    this.load();
    if (this.grow && this.$refs.content) {
      this.observer = new ResizeObserver(() => {
        this.$emit('content-height', this.$refs.content.scrollHeight + this.frameExtra);
      });
      this.observer.observe(this.$refs.content);
    }
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onFrameMessage);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    setLocal(name, value) {
      this.local = { ...this.local, [name]: value };
    },
    fetchInput(name) {
      const body = { placement: this.placement.id, input: name, params: this.filters, host: this.host, local: this.local };
      const request = () => (this.pageId
        ? axios.post(`/api/impact_analytics/agent-pages/${this.pageId}/rows/`, body)
        : axios.post(`/api/impact_analytics/agent-sessions/${this.sessionId}/page-rows/`, {
          ...body, spec: this.spec,
          widgets: this.widget ? [{ slug: this.placement.widget, contract: this.widget.contract || {} }] : [],
        }));
      const shared = (this.placement.inputs[name] || {}).source;
      if (!shared || !this.pageRows) return request();
      return this.pageRows(`${shared}|${JSON.stringify(this.scope)}`, request);
    },
    onFrameMessage(event) {
      const frame = this.$refs.htmlFrame;
      if (!frame || event.source !== frame.contentWindow || !(event.data || {}).kaironExport) return;
      this.exportSheets(event.data.kaironExport);
    },
    exportSheets(spec) {
      const sheets = (Array.isArray(spec.sheets) ? spec.sheets : [])
        .filter((sheet) => sheet && Array.isArray(sheet.rows) && sheet.rows.every((r) => r && typeof r === 'object' && !Array.isArray(r)));
      const total = sheets.reduce((n, sheet) => n + sheet.rows.length, 0);
      if (!sheets.length || total > EXPORT_MAX_ROWS) return;
      const base = String(spec.filename || this.placement.id || 'export').replace(/[^\w .-]+/g, '').trim().slice(0, 80) || 'export';
      const names = sheets.map((sheet, i) => String(sheet.name || `Sheet ${i + 1}`).replace(/[\\/?*[\]:]/g, '').slice(0, 31) || `Sheet ${i + 1}`);
      exportToExcel(sheets.map((sheet) => sheet.rows), `${base}.xlsx`, { sheetNames: names });
    },
    refresh() {
      if (!this.inputNames.length) {
        this.remounts += 1;
        return;
      }
      if (this.sharedSources.length && this.forgetSources) this.forgetSources(this.sharedSources, this.scope);
      this.load();
    },
    async load() {
      const names = this.inputNames;
      if (this.preloaded) {
        this.inputs = this.preloaded;
        const failed = names.find((name) => (this.preloaded[name] || {}).error);
        this.error = failed ? `${failed}: ${this.preloaded[failed].error}` : '';
        this.ready = true;
        this.reloads += 1;
        return;
      }
      if (!names.length) {
        this.inputs = {};
        this.ready = true;
        return;
      }
      this.loading = true;
      this.error = '';
      const token = (this.loadToken = (this.loadToken || 0) + 1);
      try {
        const results = await Promise.all(names.map((name) => this.fetchInput(name)
          .then(({ data }) => [name, { rows: data.rows || [], columns: data.columns || [], chart: data.chart || null }])
          .catch((err) => [name, { rows: [], columns: [], error: err?.response?.data?.error || 'Rows failed to load.' }])));
        if (token !== this.loadToken) return;
        const inputs = {};
        results.forEach(([name, value]) => { inputs[name] = value; });
        this.inputs = inputs;
        const failed = results.find(([, v]) => v.error);
        this.error = failed ? `${failed[0]}: ${failed[1].error}` : '';
        this.ready = true;
        this.reloads += 1;
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.wh { position: relative; width: 100%; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; background: #fff; border: 1px solid #e6e4df; border-radius: 8px; overflow: hidden; }
.wh-head { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; height: 34px; border-bottom: 1px solid #f0efec; }
.wh-title { font-size: 12.5px; font-weight: 600; color: #1a1918; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wh-bare { border: 0; border-radius: 0; background: transparent; overflow: visible; }
.wh-body { flex: 1; min-height: 0; position: relative; box-sizing: border-box; }
.wh-frame { width: 100%; height: 100%; border: 0; display: block; }
.wh-content { height: 100%; }
.wh-content-grow { height: auto; }
.wh-error { padding: 12px; font-size: 12px; color: #c23a28; }
.wh-loading { padding: 12px; font-size: 12px; color: #8b877f; }
.wh-spin { width: 10px; height: 10px; border: 2px solid #e6e4df; border-top-color: #EF6C3B; border-radius: 50%; animation: wh-spin 0.8s linear infinite; }
@keyframes wh-spin { to { transform: rotate(360deg); } }
.wh-tools { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.wh-refresh { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: #8b877f; cursor: pointer; opacity: 0; transition: opacity 0.15s, background 0.15s; }
.wh:hover .wh-refresh, .wh-refresh:focus-visible { opacity: 1; }
.wh-refresh:hover { background: #f0efec; color: #1a1918; }
.wh-refresh:disabled { cursor: default; }
.wh-refresh-float { position: absolute; top: 6px; right: 6px; z-index: 2; background: rgba(255, 255, 255, 0.9); box-shadow: 0 1px 3px rgba(26, 25, 24, 0.12); }
.wh-refresh-float.is-busy { opacity: 1; animation: wh-spin 0.8s linear infinite; }
</style>
